import {createRef, KeyboardEvent, PureComponent, RefObject} from "react";

import Elt from "./Elt";
import {s2rc, rc2s, offset} from "./rc";

import "./Matrix.css"
import { char } from "./serialization";

const minSize = 13, maxSize = 50;

type MatrixProps = {
  data : Record<string,string>;
  onChange : (coord : string, value : char) => boolean;
}

type MatrixState = {
  size : number;
  firstRow : number;
  firstCol : number;
  focussed : string;
}

class Matrix extends PureComponent<MatrixProps, MatrixState> {
  state : MatrixState = {
    size: minSize,
    firstRow: 0,
    firstCol: 0,
    focussed: "6,6",
  }

  // calculate matrix shape based on focus and content bounds.
  static getDerivedStateFromProps(props : MatrixProps, state : MatrixState) : MatrixState|null {
    let bounds = {
      row: {
        min: Number.MAX_SAFE_INTEGER,
        max: Number.MIN_SAFE_INTEGER,
        size: 0,
      },
      col: {
        min: Number.MAX_SAFE_INTEGER,
        max: Number.MIN_SAFE_INTEGER,
        size: 0,
      }
    }

    const {row, col} = s2rc(state.focussed);
    if (row < bounds.row.min) bounds.row.min = row;
    if (row > bounds.row.max) bounds.row.max = row;
    if (col < bounds.col.min) bounds.col.min = col;
    if (col > bounds.col.max) bounds.col.max = col;

    Object.entries(props.data).forEach(([coord, value]) => {
      if (value === "") return;

      const {row, col} = s2rc(coord);
      if (row < bounds.row.min) bounds.row.min = row;
      if (row > bounds.row.max) bounds.row.max = row;
      if (col < bounds.col.min) bounds.col.min = col;
      if (col > bounds.col.max) bounds.col.max = col;
    })

    bounds.row.size = bounds.row.max - bounds.row.min + 1;
    bounds.col.size = bounds.col.max - bounds.col.min + 1;

    if (bounds.row.size < minSize) {
      bounds.row.min = Math.max(Math.min(0, bounds.row.min), bounds.row.min + bounds.row.size - minSize);
      bounds.row.size = minSize;
    }

    if (bounds.col.size < minSize) {
      bounds.col.min = Math.max(Math.min(0, bounds.col.min), bounds.col.min + bounds.col.size - minSize);
      bounds.col.size = minSize;
    }

    const finalSize = Math.max(bounds.row.size, bounds.col.size)
    const sizeShift = finalSize - state.size;
    const colShift = bounds.col.min - state.firstCol;
    const rowShift = bounds.row.min - state.firstRow;

    // TODO: rest focus to something sensible when size is too big.
    if (finalSize > maxSize) return null;

    if (sizeShift === 0 && colShift === 0 && rowShift === 0) return null;

    return {
      ...state,
      size: state.size + sizeShift,
      firstRow: state.firstRow + rowShift,
      firstCol: state.firstCol + colShift,
    };
  }

  constructor(props: MatrixProps) {
    super(props);

    this.handleKeyDown = this.handleKeyDown.bind(this);
    this.focusElt = this.focusElt.bind(this);
    this.handleEltChanged = this.handleEltChanged.bind(this);
    this.setFocussed = this.setFocussed.bind(this);
  }

  get minRow() : number {
    return this.state.firstRow;
  }

  get maxRow() : number {
    return this.state.firstRow + this.state.size;
  }

  get minCol() : number {
    return this.state.firstCol;
  }

  get maxCol() : number {
    return this.state.firstCol + this.state.size;
  }

  private myRefs : Record<string, RefObject<Elt>> = {};

  getRef(coord : string) : RefObject<Elt> {
    let ref = this.myRefs[coord];
    if (!ref) this.myRefs[coord] = ref = createRef<Elt>();
    return ref;
  }

  private focusNextElt = this.focusEltRight;
  private focusPrevElt = this.focusEltLeft;

  focusEltUp = () => {
    this.focusElt(offset(this.state.focussed, [-1, 0]));
  };

  focusEltDown() : void {
    this.focusElt(offset(this.state.focussed, [+1, 0]));
  };

  focusEltLeft() : void {
    this.focusElt(offset(this.state.focussed, [0, -1]));
  };

  focusEltRight() : void {
    this.focusElt(offset(this.state.focussed, [0, +1]));
  };

  setFocussed(coord : string) {
    this.setState({focussed: coord});
  }

  focusElt(coord : string) : void {
    const ref = this.getRef(coord);

    if (ref.current) {
      ref.current.focus();
    } else {
      // Set focus outside current matrix. We'll adjust matrix size before next render and then trigger the focus.
      this.setFocussed(coord);
      this.componentDidUpdate = () => {
        this.componentDidUpdate = () => {};
        ref.current?.focus();
      };
    }
  }

  private handleEltChanged(coord : string, c : char) : boolean {
    if (!this.props.onChange(coord, c)) {
      return false;
    }

    if (c === "") {
      this.focusPrevElt();
    } else {
      this.focusNextElt();
    }

    return true;
  }

  // handle arrow keys.
  handleKeyDown(event : KeyboardEvent<HTMLDivElement>) : void {
    switch (event.key) {
    case "ArrowUp":
      this.focusEltUp();
      this.focusNextElt = this.focusEltRight;
      this.focusPrevElt = this.focusEltLeft;
      break;
    case "ArrowDown":
      this.focusEltDown();
      this.focusNextElt = this.focusEltDown;
      this.focusPrevElt = this.focusEltUp;
      break;
    case "ArrowLeft":
      this.focusEltLeft();
      this.focusNextElt = this.focusEltRight;
      this.focusPrevElt = this.focusEltLeft;
      break;
    case "ArrowRight":
      this.focusEltRight();
      this.focusNextElt = this.focusEltRight;
      this.focusPrevElt = this.focusEltLeft;
      break;
    }
  }

  render() : JSX.Element {
    console.log("rendering matrix");

    let elts: JSX.Element[] = [];

    for (let row = this.minRow; row < this.maxRow; row++) {
      for (let col = this.minCol; col < this.maxCol; col++) {
        const coord = rc2s(row, col);

        elts.push(
          <Elt
            coord={coord}
            onChange={this.handleEltChanged}
            onFocus={this.setFocussed}
            key={coord}
            ref={this.getRef(coord)}
          >
            {this.props.data[coord]||""}
          </Elt>
        );
      }
    }

    return (
      <div className={`Matrix Matrix-${this.state.size}`} onKeyDown={this.handleKeyDown}>
        {elts}
      </div>
    );
  }
}

export default Matrix;